import React, { useState, useEffect } from 'react'
import { searchAccounts, getAccountContacts } from '../services/salesforce'

const CustomerSelector = ({ selectedAccount, selectedContact, onAccountChange, onContactChange }) => {
  const [accountSearch, setAccountSearch] = useState('')
  const [accounts, setAccounts] = useState([])
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  // Search accounts when user types
  useEffect(() => {
    const searchTimer = setTimeout(() => {
      if (accountSearch.length > 2) {
        handleAccountSearch()
      } else {
        setAccounts([])
      }
    }, 300)

    return () => clearTimeout(searchTimer)
  }, [accountSearch])

  // Load contacts when account changes
  useEffect(() => {
    if (selectedAccount) {
      loadContacts()
    } else {
      setContacts([])
      onContactChange(null)
    }
  }, [selectedAccount])

  const handleAccountSearch = async () => {
    try {
      setLoading(true)
      const results = await searchAccounts(accountSearch)
      setAccounts(results)
      setShowDropdown(true)
    } catch (error) {
      console.error('Error searching accounts:', error)
      // Use demo data if API fails
      setAccounts([
        { Id: '001XX000003DHPh', Name: 'Acme Corporation', BillingStreet: '123 Main St', BillingCity: 'San Francisco', BillingPostalCode: '94105' },
        { Id: '001XX000003DHPi', Name: 'Global Tech Inc', BillingStreet: '456 Tech Ave', BillingCity: 'San Jose', BillingPostalCode: '95110' }
      ])
    } finally {
      setLoading(false)
    }
  }

  const loadContacts = async () => {
    try {
      setLoading(true)
      const results = await getAccountContacts(selectedAccount.Id)
      setContacts(results)
    } catch (error) {
      console.error('Error loading contacts:', error)
      // Use demo data if API fails
      setContacts([
        { Id: '003XX000004TMM2', Name: 'John Doe', Title: 'CEO', Email: 'john@example.com' },
        { Id: '003XX000004TMM3', Name: 'Jane Smith', Title: 'CTO', Email: 'jane@example.com' }
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleAccountSelect = (account) => {
    onAccountChange(account)
    setAccountSearch(account.Name)
    setShowDropdown(false)
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Customer Information</h2>
      
      {/* Account Search */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Company Account
        </label>
        <input
          type="text"
          value={accountSearch}
          onChange={(e) => setAccountSearch(e.target.value)}
          onFocus={() => accountSearch.length > 2 && setShowDropdown(true)}
          placeholder="Search for account..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
        
        {/* Account Dropdown */}
        {showDropdown && accounts.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
            {accounts.map((account) => (
              <div
                key={account.Id}
                onClick={() => handleAccountSelect(account)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
              >
                <div className="font-medium text-gray-900">{account.Name}</div>
                <div className="text-sm text-gray-500">
                  {account.BillingStreet}, {account.BillingCity} {account.BillingPostalCode}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Account Info */}
      {selectedAccount && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Selected Account</h3>
          <div className="text-sm text-gray-600">
            <p><strong>Company:</strong> {selectedAccount.Name}</p>
            <p><strong>Address:</strong> {selectedAccount.BillingStreet}, {selectedAccount.BillingCity} {selectedAccount.BillingPostalCode}</p>
          </div>
        </div>
      )}

      {/* Contact Selection */}
      {selectedAccount && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Person
          </label>
          <select
            value={selectedContact?.Id || ''}
            onChange={(e) => {
              const contact = contacts.find(c => c.Id === e.target.value)
              onContactChange(contact)
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={loading || contacts.length === 0}
          >
            <option value="">Select a contact...</option>
            {contacts.map((contact) => (
              <option key={contact.Id} value={contact.Id}>
                {contact.Name} - {contact.Title}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}

export default CustomerSelector